'use client';

import { useEffect, type ComponentType } from 'react';

import { Flex, Spinner, Container, Spacer } from '@backyard-ui/core';
import { ErrorBoundary } from 'react-error-boundary';
import { useInView } from 'react-intersection-observer';

import { ErrorReporter } from '@/infra/monitoring/error-reporter';
import {
  type PageType,
  type Response as ModulesType,
} from '@/infra/services/modular-content/get-modules';
import { type ModularContent as ModularContentType } from '@/infra/services/modular-content/model';
import { useGetModulesInfinite } from '@/presenters/hooks/modular-content/useGetModulesInfinite';
import { useUserResources } from '@/presenters/hooks/user';
import { USER_TRACKING_ID } from '@/utils/constants/cookies';
import { cookies } from '@/utils/cookies';

import { ProductsCarousel } from '../common/ProductsCarousel';

import {
  Banners,
  ContentGallery,
  Informative,
  LastContents,
  LeadCapture,
  Markdown,
  RecommendedProductsCarousel,
  RetailMediaBanner,
  RetailMediaProductsCarousel,
  Slide,
  Thumbnails,
  VouchersCarousel,
} from './components';

interface ModularContentProps {
  initialModules: ModulesType;
  slug: string;
  pageType: PageType;
}

const modulesWithoutContainer: ModularContentType.Module['type'][] = ['slide'];

interface ModulesComponentsType {
  // It's ok to use 'any' here because in this case it's checked at component level
  [key: string]: ComponentType<any>;
}

const isVoucherModuleEnabled =
  process.env.NEXT_PUBLIC_FEATURE_FLIP_MODULAR_VOUCHERS_ENABLED === 'true';

const modulesComponents: ModulesComponentsType = {
  markdown: Markdown,
  slide: Slide,
  banner: Banners,
  informative: Informative,
  leadCapture: LeadCapture,
  thumbnail: Thumbnails,
  lastContents: LastContents,
  beInspiredContents: ContentGallery,
  products: ProductsCarousel,
  recommendedProducts: RecommendedProductsCarousel,
  vouchers: isVoucherModuleEnabled ? VouchersCarousel : () => null,
  retailMediaSponsoredBanner: RetailMediaBanner,
  retailMediaSponsoredProducts: RetailMediaProductsCarousel,
};

export default function ModularContent(props: ModularContentProps) {
  const { initialModules, slug, pageType } = props;

  const { data: userTrackingId, isFetched: hasFetchedUserResources } =
    useUserResources((data) => data.trackingId);

  const { data, fetchNextPage, isFetchingNextPage } = useGetModulesInfinite(
    { slug, pageType },
    {
      initialData: {
        pages: [initialModules],
        pageParams: [1],
      },
    }
  );

  const { ref, inView } = useInView();

  useEffect(() => {
    if (!inView || isFetchingNextPage) return;

    fetchNextPage();
  }, [inView, fetchNextPage, isFetchingNextPage]);

  useEffect(() => {
    if (!hasFetchedUserResources) return;

    const cookieOptions = cookies.createOptions({
      name: USER_TRACKING_ID,
      value: userTrackingId ?? '',
    });

    cookies.client.set(cookieOptions);
  }, [userTrackingId, hasFetchedUserResources]);

  if (!Boolean(data?.pages.length)) {
    return null;
  }

  return (
    <>
      <div>
        {data?.pages.map((page) => {
          if (!Boolean(page.data)) {
            return null;
          }

          return page.data?.results.map((item, index) => {
            if (!(item.type in modulesComponents)) return null;

            const Module = modulesComponents[item.type];

            const safeModule = (
              <ErrorBoundary
                key={index}
                fallback={<></>}
                onError={(error) =>
                  ErrorReporter.report(error, {
                    slug,
                    item,
                  })
                }
              >
                <Module
                  {...item}
                  slug={slug}
                  pageType={pageType}
                  index={index}
                />
              </ErrorBoundary>
            );

            const moduleComponent = modulesWithoutContainer.includes(
              item.type
            ) ? (
              safeModule
            ) : (
              <Container size="7xl" className="bg-white">
                {safeModule}
              </Container>
            );

            const spacings =
              'spacings' in item ? item.spacings : { top: true, bottom: true };

            return (
              <>
                {spacings.top && (
                  <Spacer
                    size="8"
                    axis="vertical"
                    className="bg-white"
                    data-testid={`top-spacer-${index}`}
                  />
                )}

                <section key={index} data-testid="module-wrapper">
                  {moduleComponent}
                </section>

                {spacings.bottom && (
                  <Spacer
                    size="8"
                    axis="vertical"
                    className="bg-white"
                    data-testid={`bottom-spacer-${index}`}
                  />
                )}
              </>
            );
          });
        })}
      </div>

      {isFetchingNextPage ? (
        <Flex align="center" justify="center" data-testid="spinner">
          <Spinner size="xl" appearance="primary" UNSAFE_className="m-10" />
        </Flex>
      ) : (
        <div ref={ref} className="h-1" data-testid="intersector-ref" />
      )}
    </>
  );
}
